import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext.jsx';
import { userService } from '../../services/userService';
import { toastService } from '../../services/toastService';
import { Input } from '../../components/ui/input.jsx';
import { Button } from '../../components/buttons/button.jsx';
import PageLayout from '../../components/layout/PageLayout';
import FormPageSkeleton from '../../components/skeletons/ProfilePageSkeleton.jsx';
import { ICONS } from '../../constants/imageConstants';
import ImageCropModal from '../../components/modals/ImageCropModal.jsx';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    phone_number: null,
  });
  const [originalData, setOriginalData] = useState({
    first_name: '',
    last_name: '',
    phone_number: null,
  });

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [imageSrcForCrop, setImageSrcForCrop] = useState(null);
  const [localImageFile, setLocalImageFile] = useState(null);

  useEffect(() => {
    if (user) {
      const userData = {
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number ?? '',
      };
      setProfileData(userData);
      setOriginalData(userData);
      setImagePreview(user.profile_image_local || user.profile_image);
      setIsLoading(false);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if ((name === 'first_name' || name === 'last_name') && /\d/.test(value)) {
      return;
    }
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrcForCrop(reader.result?.toString() || '');
      setCropModalOpen(true);
    });
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropComplete = (croppedBlob) => {
    const croppedFile = new File([croppedBlob], 'profile.jpg', { type: 'image/jpeg' });
    setImageFile(croppedFile);
    const previewUrl = URL.createObjectURL(croppedFile);
    setImagePreview(previewUrl);
    setCropModalOpen(false);
    setLocalImageFile(croppedFile);
  };

  const hasChanges = () => {
    const dataChanged = Object.keys(originalData).some(key => 
      profileData[key] !== originalData[key]
    );
    return dataChanged || imageFile;
  };

  const handleUpdate = () => {
    if (isSubmitting) return;

    if (!hasChanges()) {
      toastService.info('No changes detected');
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    Object.keys(profileData).forEach((key) =>
      formData.append(key, profileData[key] || '')
    );
    if (imageFile) {
      formData.append('profile_image', imageFile);
    }

    const updatePromise = userService.updateAccountDetails(formData);

    toastService
      .notifyPromise(updatePromise, {
        pending: 'Updating profile...',
        success: 'Profile updated successfully!',
        error: (err) =>
          err?.response?.data?.message || 'Failed to update profile.',
      })
      .then((updatedUser) => {
        if (updatedUser) {
          updateUser(updatedUser, localImageFile ? URL.createObjectURL(localImageFile) : null);
        }
        navigate('/settings');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={imageSrcForCrop}
        onCropComplete={handleCropComplete}
      />
      
      <PageLayout title="Edit Profile" showBackButton showStatus>
        {isLoading ? (
          <FormPageSkeleton />
        ) : (
          <div className="space-y-8 pb-6">
            <div className="flex flex-col items-center gap-4 pt-2">
              <label className="relative cursor-pointer group">
                <div className="w-28 h-28 rounded-full bg-gradient-to-br from-gray-100 to-gray-50 overflow-hidden border-2 border-gray-300 shadow-md hover:shadow-lg transition-all duration-200">
                  <img
                    src={imagePreview || ICONS.USER_AVATAR}
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = ICONS.USER_AVATAR;
                    }}
                  />
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="profile-image-upload"
                />
                <label
                  htmlFor="profile-image-upload"
                  className="absolute bottom-1 right-1 w-9 h-9 bg-white border-2 border-gray-400 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-all duration-200 shadow-md"
                >
                  <img src={ICONS.PROFILE_EDIT} alt="Edit" className="w-4 h-4" />
                </label>
              </label>
              <p className="text-xs text-gray-500 font-medium text-center">Click to update profile picture</p>
            </div>

            <div className="space-y-5">
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Personal Information</h3>
                <div className="space-y-4">
                  <Input
                    label="First Name"
                    name="first_name"
                    value={profileData.first_name}
                    onChange={handleInputChange}
                    pattern="[A-Za-z\s]*"
                    title="Only letters are allowed"
                    placeholder="Enter first name"
                  />
                  <Input
                    label="Last Name"
                    name="last_name"
                    value={profileData.last_name}
                    onChange={handleInputChange}
                    pattern="[A-Za-z\s]*"
                    title="Only letters are allowed"
                    placeholder="Enter last name"
                  />
                  <Input
                    label="Phone Number"
                    name="phone_number"
                    value={profileData.phone_number ?? ''}
                    onChange={handleInputChange}
                    type="tel"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">Account Details</h3>
                <div className="space-y-4">
                  <Input
                    label="Email Address"
                    name="email"
                    value={user?.email || ''}
                    readOnly
                    disabled
                  />
                  <Input
                    label="Region"
                    name="address"
                    value={user?.address || ''}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <Button
                onClick={handleUpdate}
                variant="primary"
                fullWidth
                disabled={isSubmitting || !hasChanges()}
                className="h-12 font-semibold"
              >
                {isSubmitting ? 'Updating...' : 'Update Profile'}
              </Button>
            </div>
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default ProfilePage;